<template>
  <transition name="modal-fade">
    <div
        v-if="show"
        class="modal-overlay"
        @click.self="handleClose"
    >
      <div class="modal-container">
        <!-- Modal Header -->
        <div class="modal-header">
          <div class="header-content">
            <div class="header-icon">
              <svg
                  class="tw-w-8 tw-h-8 tw-text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
              >
                <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            </div>
            <div>
              <h2 class="modal-title">
                Request Your Quote
              </h2>
              <p class="modal-subtitle">
                {{
                  cartItemsCount > 0 ? `Complete your request for ${cartItemsCount} item${cartItemsCount > 1 ? 's' : ''}` : 'Fill in your details to receive a personalized quote'
                }}
              </p>
            </div>
          </div>
          <button
              class="close-button"
              aria-label="Close modal"
              @click="handleClose"
          >
            <svg
                class="tw-w-6 tw-h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
            >
              <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <!-- Modal Body -->
        <form
            class="modal-body"
            @submit.prevent="handleSubmit"
        >
          <!-- Progress Indicator -->
          <div
              v-if="leadFields && leadFields.length > 3"
              class="progress-bar"
          >
            <div
                class="progress-fill"
                :style="{ width: progressPercentage + '%' }"
            />
          </div>

          <!-- Form Fields -->
          <div class="form-grid">
            <div
                v-for="field in leadFields"
                :key="field.field_name"
                class="form-group"
            >
              <label
                  :for="field.field_name"
                  class="form-label"
              >
                {{ formatFieldName(field.field_name) }}
                <span
                    v-if="field.required"
                    class="required-indicator"
                >*</span>
              </label>
              <div class="input-wrapper">
                <input
                    v-if="getFieldType(field.field_name) !== 'textarea'"
                    :id="field.field_name"
                    v-model="formData[field.field_name]"
                    :type="getFieldType(field.field_name)"
                    :required="field.required"
                    :placeholder="getFieldPlaceholder(field.field_name)"
                    class="form-input"
                    :class="{ 'has-error': fieldErrors[field.field_name] }"
                    @input="updateProgress"
                >
                <textarea
                    v-else
                    :id="field.field_name"
                    v-model="formData[field.field_name]"
                    :required="field.required"
                    :placeholder="getFieldPlaceholder(field.field_name)"
                    rows="4"
                    class="form-input"
                    :class="{ 'has-error': fieldErrors[field.field_name] }"
                    @input="updateProgress"
                />
                <span
                    v-if="fieldErrors[field.field_name]"
                    class="error-message"
                >
                  {{ fieldErrors[field.field_name] }}
                </span>
              </div>
            </div>
          </div>

          <!-- Product Summary (if items in cart) -->
          <div
              v-if="cartItemsCount > 0 && productDetails"
              class="product-summary"
          >
            <h3 class="summary-title">
              Quote Summary
            </h3>
            <div class="summary-items">
              <div
                  v-if="Array.isArray(productDetails)"
                  class="summary-list"
              >
                <div
                    v-for="(item, idx) in productDetails"
                    :key="idx"
                    class="summary-item"
                >
                  <span class="item-name">{{ item.name }}</span>
                  <span class="item-quantity">Qty: {{ item.quantity || 1 }}</span>
                </div>
              </div>
              <div
                  v-else-if="productDetails"
                  class="summary-item"
              >
                <span class="item-name">{{ productDetails.name }}</span>
                <span class="item-quantity">Qty: 1</span>
              </div>
            </div>
          </div>

          <!-- Form Actions -->
          <div class="modal-footer">
            <div class="footer-info">
              <svg
                  class="tw-w-4 tw-h-4 tw-text-gray-400"
                  fill="currentColor"
                  viewBox="0 0 20 20"
              >
                <path
                    fill-rule="evenodd"
                    d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                    clip-rule="evenodd"
                />
              </svg>
              <span class="tw-text-xs tw-text-gray-500">Your information is secure and will never be shared</span>
            </div>
            <div class="button-group">
              <button
                  type="button"
                  class="btn-cancel"
                  @click="handleClose"
              >
                Cancel
              </button>
              <button
                  type="submit"
                  class="btn-submit"
                  :disabled="isSubmitting"
                  :style="{ backgroundColor: primaryColor }"
              >
                <span
                    v-if="!isSubmitting"
                    class="tw-flex tw-items-center"
                >
                  <svg
                      class="tw-w-5 tw-h-5 tw-mr-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                  >
                    <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
                    />
                  </svg>
                  Submit Quote Request
                </span>
                <span
                    v-else
                    class="tw-flex tw-items-center"
                >
                  <svg
                      class="tw-animate-spin tw-h-5 tw-w-5 tw-mr-2"
                      fill="none"
                      viewBox="0 0 24 24"
                  >
                    <circle
                        class="tw-opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        stroke-width="4"
                    />
                    <path
                        class="tw-opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Submitting...
                </span>
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  </transition>
</template>

<script>
export default {
  name: 'QuoteFormModal',
  emits: ['submit', 'close'],
  props: {
    show: {
      type: Boolean,
      default: false
    },
    leadFields: {
      type: Array,
      default: () => []
    },
    cartItemsCount: {
      type: Number,
      default: 0
    },
    productDetails: {
      type: [Array, Object],
      default: null
    },
    primaryColor: {
      type: String,
      default: '#10b981'
    }
  },
  data() {
    return {
      formData: {},
      fieldErrors: {},
      isSubmitting: false,
      filledFields: 0
    };
  },
  computed: {
    progressPercentage() {
      if (!this.leadFields || this.leadFields.length === 0) return 0;
      return (this.filledFields / this.leadFields.length) * 100;
    }
  },
  watch: {
    show(newVal) {
      if (newVal) {
        this.initializeForm();
        // Prevent body scroll when modal is open
        document.body.style.overflow = 'hidden';
      } else {
        // Re-enable body scroll when modal is closed
        document.body.style.overflow = '';
      }
    }
  },
  beforeUnmount() {
    // Clean up body overflow style
    document.body.style.overflow = '';
  },
  methods: {
    initializeForm() {
      this.formData = {};
      this.fieldErrors = {};
      this.filledFields = 0;

      if (this.leadFields) {
        this.leadFields.forEach(field => {
          this.formData[field.field_name] = '';
        });
      }
    },
    formatFieldName(fieldName) {
      return fieldName
          .split('_')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
    },
    getFieldType(fieldName) {
      const name = fieldName.toLowerCase();
      if (name.includes('email')) return 'email';
      if (name.includes('phone') || name.includes('tel')) return 'tel';
      if (name.includes('message') || name.includes('notes') || name.includes('comments')) return 'textarea';
      if (name.includes('company')) return 'text';
      return 'text';
    },
    getFieldPlaceholder(fieldName) {
      const name = fieldName.toLowerCase();
      if (name.includes('email')) return 'john@example.com';
      if (name.includes('phone')) return '(555) 123-4567';
      if (name.includes('first_name')) return 'John';
      if (name.includes('last_name')) return 'Doe';
      if (name.includes('company')) return 'Acme Inc.';
      if (name.includes('message')) return 'Tell us about your project...';
      return `Enter ${this.formatFieldName(fieldName).toLowerCase()}`;
    },
    updateProgress() {
      this.filledFields = Object.values(this.formData).filter(val => val && val.trim() !== '').length;
    },
    validateForm() {
      let isValid = true;
      this.fieldErrors = {};

      this.leadFields.forEach(field => {
        if (field.required && (!this.formData[field.field_name] || this.formData[field.field_name].trim() === '')) {
          this.fieldErrors[field.field_name] = `${this.formatFieldName(field.field_name)} is required`;
          isValid = false;
        }

        // Email validation
        if (field.field_name.toLowerCase().includes('email') && this.formData[field.field_name]) {
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(this.formData[field.field_name])) {
            this.fieldErrors[field.field_name] = 'Please enter a valid email address';
            isValid = false;
          }
        }

        // Phone validation
        if (field.field_name.toLowerCase().includes('phone') && this.formData[field.field_name]) {
          const phoneRegex = /^[\d\s\-()+]+$/;
          if (!phoneRegex.test(this.formData[field.field_name])) {
            this.fieldErrors[field.field_name] = 'Please enter a valid phone number';
            isValid = false;
          }
        }
      });

      return isValid;
    },
    async handleSubmit() {
      if (!this.validateForm()) {
        // Scroll to first error
        const firstError = document.querySelector('.has-error');
        if (firstError) {
          firstError.scrollIntoView({behavior: 'smooth', block: 'center'});
        }
        return;
      }

      this.isSubmitting = true;

      console.log({
        formData: this.formData,
        productDetails: this.productDetails
      })

      // Emit the submit event with form data
      this.$emit('submit', {
        formData: this.formData,
        productDetails: this.productDetails
      });

      // Simulate API call delay
      setTimeout(() => {
        this.isSubmitting = false;
        this.handleClose();
      }, 1500);
    },
    handleClose() {
      if (!this.isSubmitting) {
        this.$emit('close');
      }
    }
  }
};
</script>

<style scoped>
/* Modal Overlay */
.modal-overlay {
  @apply tw-fixed tw-inset-0 tw-z-50 tw-flex tw-items-center tw-justify-center tw-p-4;
  background-color: rgba(0, 0, 0, 0.75);
  backdrop-filter: blur(4px);
}

/* Modal Container */
.modal-container {
  @apply tw-bg-white tw-rounded-2xl tw-shadow-2xl tw-w-full tw-max-w-2xl tw-max-h-[90vh] tw-overflow-hidden;
  @apply tw-flex tw-flex-col;
}

/* Modal Header */
.modal-header {
  @apply tw-px-6 tw-py-5 tw-border-b tw-border-gray-200 tw-flex tw-justify-between tw-items-start;
  background: linear-gradient(to right, #f9fafb, #ffffff);
}

.header-content {
  @apply tw-flex tw-items-start tw-gap-4;
}

.header-icon {
  @apply tw-p-2 tw-bg-green-100 tw-rounded-lg;
}

.modal-title {
  @apply tw-text-2xl tw-font-semibold tw-text-gray-900;
}

.modal-subtitle {
  @apply tw-text-sm tw-text-gray-500 tw-mt-1;
}

.close-button {
  @apply tw-p-2 tw-text-gray-400 hover:tw-text-gray-600 tw-transition-colors tw-rounded-lg hover:tw-bg-gray-100;
}

/* Modal Body */
.modal-body {
  @apply tw-px-6 tw-py-6 tw-overflow-y-auto;
  max-height: calc(90vh - 180px);
}

/* Progress Bar */
.progress-bar {
  @apply tw-w-full tw-h-2 tw-bg-gray-200 tw-rounded-full tw-mb-6 tw-overflow-hidden;
}

.progress-fill {
  @apply tw-h-full tw-bg-green-500 tw-transition-all tw-duration-300 tw-ease-out;
}

/* Form Grid */
.form-grid {
  @apply tw-grid tw-gap-5;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
}

.form-group {
  @apply tw-flex tw-flex-col;
}

.form-label {
  @apply tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-2 tw-flex tw-items-center tw-gap-1;
}

.required-indicator {
  @apply tw-text-red-500;
}

.input-wrapper {
  @apply tw-relative;
}

.form-input {
  @apply tw-w-full tw-px-4 tw-py-2.5 tw-border tw-border-gray-300 tw-rounded-lg;
  @apply tw-text-gray-900 tw-placeholder-gray-400;
  @apply tw-transition-all tw-duration-200;
  @apply focus:tw-ring-2 focus:tw-ring-green-500 focus:tw-border-transparent focus:tw-outline-none;
}

.form-input:hover {
  @apply tw-border-gray-400;
}

.form-input.has-error {
  @apply tw-border-red-500 focus:tw-ring-red-500;
}

textarea.form-input {
  @apply tw-resize-none;
}

.error-message {
  @apply tw-absolute tw-text-xs tw-text-red-500 tw-mt-1;
}

/* Product Summary */
.product-summary {
  @apply tw-mt-6 tw-p-4 tw-bg-gray-50 tw-rounded-lg tw-border tw-border-gray-200;
}

.summary-title {
  @apply tw-text-sm tw-font-semibold tw-text-gray-700 tw-mb-3;
}

.summary-items {
  @apply tw-space-y-2;
}

.summary-list {
  @apply tw-space-y-2;
}

.summary-item {
  @apply tw-flex tw-justify-between tw-items-center tw-text-sm;
}

.item-name {
  @apply tw-text-gray-600;
}

.item-quantity {
  @apply tw-text-gray-500 tw-font-medium;
}

/* Modal Footer */
.modal-footer {
  @apply tw-px-6 tw-py-4 tw-border-t tw-border-gray-200 tw-bg-gray-50;
  @apply tw-flex tw-flex-col sm:tw-flex-row tw-justify-between tw-items-center tw-gap-4;
}

.footer-info {
  @apply tw-flex tw-items-center tw-gap-2;
}

.button-group {
  @apply tw-flex tw-gap-3 tw-w-full sm:tw-w-auto;
}

/* Buttons */
.btn-cancel {
  @apply tw-px-5 tw-py-2.5 tw-bg-white tw-text-gray-700 tw-font-medium;
  @apply tw-border tw-border-gray-300 tw-rounded-lg;
  @apply tw-transition-all tw-duration-200;
  @apply hover:tw-bg-gray-50 hover:tw-border-gray-400;
  @apply focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-gray-400;
}

.btn-submit {
  @apply tw-px-6 tw-py-2.5 tw-text-white tw-font-medium;
  @apply tw-rounded-lg tw-shadow-sm;
  @apply tw-transition-all tw-duration-200;
  @apply hover:tw-shadow-md focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-offset-2;
  @apply disabled:tw-opacity-50 disabled:tw-cursor-not-allowed;
}

/* Animations */
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: all 0.3s ease;
}

.modal-fade-enter-from {
  opacity: 0;
}

.modal-fade-enter-to {
  opacity: 1;
}

.modal-fade-leave-from {
  opacity: 1;
}

.modal-fade-leave-to {
  opacity: 0;
}

.modal-fade-enter-active .modal-container {
  animation: modalSlideIn 0.3s ease-out;
}

.modal-fade-leave-active .modal-container {
  animation: modalSlideOut 0.3s ease-in;
}

@keyframes modalSlideIn {
  from {
    transform: translateY(-20px) scale(0.95);
    opacity: 0;
  }
  to {
    transform: translateY(0) scale(1);
    opacity: 1;
  }
}

@keyframes modalSlideOut {
  from {
    transform: translateY(0) scale(1);
    opacity: 1;
  }
  to {
    transform: translateY(-20px) scale(0.95);
    opacity: 0;
  }
}

/* Responsive Design */
@media (max-width: 640px) {
  .modal-container {
    @apply tw-max-w-full tw-m-4;
  }

  .modal-body {
    @apply tw-px-4;
  }

  .form-grid {
    @apply tw-grid-cols-1;
  }

  .modal-footer {
    @apply tw-flex-col;
  }

  .button-group {
    @apply tw-flex-col-reverse tw-w-full;
  }

  .btn-cancel,
  .btn-submit {
    @apply tw-w-full;
  }
}
</style>
